export const PARAM_STALE_MS = 10 * 60 * 1000;

export interface ParamHolder {
  device_id: string;
  is_principal: boolean;
  priority: number | null;
  at: number;
}

export type ParamHolders = Record<string, ParamHolder>;
export type ParamOwners = Record<string, string>;
export type ParamValues = Record<string, number | string>;

export interface ParamSender {
  device_id: string;
  is_principal: boolean;
  priority: number | null;
  at: number;
}

const SECONDARY_BASE_RANK = 2;
const UNRANKED_PRIORITY = 1000;

const rankOf = (
  key: string,
  owners: ParamOwners | null | undefined,
  holder: Pick<ParamHolder, "device_id" | "is_principal" | "priority">,
): number => {
  if (owners?.[key] === holder.device_id) return 0;
  if (holder.is_principal) return 1;
  return SECONDARY_BASE_RANK + (holder.priority ?? UNRANKED_PRIORITY);
};

const winsKey = (
  key: string,
  current: ParamHolder | undefined,
  sender: ParamSender,
  owners: ParamOwners | null | undefined,
): boolean => {
  if (!current || current.device_id === sender.device_id) return true;
  if (sender.at - current.at > PARAM_STALE_MS) return true;
  return rankOf(key, owners, sender) <= rankOf(key, owners, current);
};

export const mergeRankedParams = (
  existing: ParamValues | undefined,
  holders: ParamHolders | undefined,
  incoming: Record<string, number | string | null> | null | undefined,
  sender: ParamSender,
  owners?: ParamOwners | null,
): { params: ParamValues; holders: ParamHolders; changed: boolean } => {
  const base = existing ?? {};
  const baseHolders = holders ?? {};
  if (!incoming || typeof incoming !== "object") {
    return { params: base, holders: baseHolders, changed: false };
  }

  const entries = Object.entries(incoming).filter(
    ([, value]) => typeof value === "number" || typeof value === "string",
  ) as [string, number | string][];
  const accepted = entries.filter(([key]) => winsKey(key, baseHolders[key], sender, owners));
  const changedValues = accepted.filter(([key, value]) => base[key] !== value);
  const changedHolders = accepted.filter(([key]) => {
    const holder = baseHolders[key];
    return !holder || holder.device_id !== sender.device_id || holder.at !== sender.at;
  });

  if (changedValues.length === 0 && changedHolders.length === 0) {
    return { params: base, holders: baseHolders, changed: false };
  }

  const nextHolder: ParamHolder = {
    device_id: sender.device_id,
    is_principal: sender.is_principal,
    priority: sender.priority,
    at: sender.at,
  };

  return {
    params: { ...base, ...Object.fromEntries(accepted) },
    holders: { ...baseHolders, ...Object.fromEntries(accepted.map(([key]) => [key, nextHolder])) },
    changed: changedValues.length > 0,
  };
};
